import {
  Box,
  BoxProps,
  Center,
  CenterProps,
  Flex,
  FlexProps,
  FormControl,
  FormLabel,
  Skeleton,
  Stat,
  StatLabel,
  StatLabelProps,
  StatNumber,
  StatProps,
} from "@chakra-ui/react";
import { makeFormatCurrency } from "utils/formatting";
import { InputComponentProps } from "./pricingCalculatorUtils";
import PricingPercentInput from "./PricingPercentInput";

const SPACING_X = 2;

export function PricingCalculatorSectionContainer({
  children,
  ...rest
}: FlexProps) {
  return (
    <Flex as="section" flexWrap="wrap" mx={SPACING_X * -1} {...rest}>
      {children}
    </Flex>
  );
}

type PricingCalculatorInputProps = BoxProps & {
  children: React.ReactNode;
};

export function PricingCalculatorInput(props: PricingCalculatorInputProps) {
  return (
    <Box flex="1 1 67%" my={2} mx={SPACING_X} minWidth="12rem" {...props} />
  );
}

type PricingCalculatorStatProps = StatProps & {
  isLoading?: boolean;
  label: React.ReactNode;
  labelProps?: StatLabelProps;
  value: React.ReactNode;
};

export function PricingCalculatorStat({
  isLoading,
  label,
  labelProps,
  value,
  ...rest
}: PricingCalculatorStatProps) {
  return (
    <Stat
      size="lg"
      fontFamily="heading"
      lineHeight="1.1"
      color="inherit"
      sx={{
        "& > dl": {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          mb: 2,
        },
      }}
      {...rest}
    >
      {label && (
        <StatLabel
          flex="1 1 0"
          maxWidth="16em"
          textAlign="right"
          {...labelProps}
        >
          {label}
        </StatLabel>
      )}

      <Skeleton
        as={StatNumber}
        isLoaded={!isLoading}
        minWidth="4em"
        textAlign="right"
        whiteSpace="nowrap"
        ml={4}
      >
        {value || " "}
      </Skeleton>
    </Stat>
  );
}

type PricingCalculatorOutputProps = CenterProps & {
  children?: React.ReactNode;
  isStatNumberLoading?: boolean;
  statLabel?: React.ReactNode;
  statNumber?: React.ReactNode;
};

export function PricingCalculatorOutput({
  children,
  isStatNumberLoading = false,
  statLabel,
  statNumber,
  ...rest
}: PricingCalculatorOutputProps) {
  return (
    <Center
      flex="100 1 0"
      justifyContent="flex-end"
      my={2}
      mx={SPACING_X}
      px={6}
      py={2}
      color="magenta.veryDark"
      borderRadius="md"
      {...rest}
    >
      <Box>
        <PricingCalculatorStat
          label={statLabel}
          value={statNumber}
          isLoading={isStatNumberLoading}
        />
        {children}
      </Box>
    </Center>
  );
}

type Props = InputComponentProps & {
  InputComponent?: React.ComponentType<InputComponentProps>;
  afterFormLabel?: React.ReactNode;
  inputLabel: React.ReactNode;
  isLoading?: boolean;
  isOutputHighlighted?: boolean;
  outputContentAfter?: React.ReactNode;
  outputLabel?: React.ReactNode;
  outputValue?: string | number | null;
};

export default function PricingCalculatorSection({
  InputComponent = PricingPercentInput,
  afterFormLabel,
  formContext,
  inputLabel,
  isLoading,
  isOutputHighlighted,
  name,
  outputContentAfter,
  outputLabel,
  outputValue,
}: Props) {
  return (
    <PricingCalculatorSectionContainer>
      <PricingCalculatorInput
        as={FormControl}
        id={`PricingCalculatorSection-${name}`}
        backgroundColor="cardBackground"
        px={10}
        py={8}
        mt={1}
        mb={2}
        borderRadius="xl"
        display="flex"
        flexWrap="wrap"
        alignItems="center"
      >
        <Flex flex="20 1 0" minWidth="7em" flexWrap="wrap" my={1}>
          <FormLabel
            fontFamily="heading"
            fontSize="lg"
            fontWeight="bold"
            color="magenta.veryDark"
            my={0}
          >
            {inputLabel}
          </FormLabel>
          {afterFormLabel}
        </Flex>
        <InputComponent
          formContext={formContext}
          name={name}
          loading={isLoading}
          flex="1 1 80%"
          ml="auto"
        />
      </PricingCalculatorInput>

      <PricingCalculatorOutput
        id={`PricingCalculatorSection-${name}`}
        statLabel={outputLabel}
        statNumber={makeFormatCurrency({ minimumFractionDigits: 2 })(
          outputValue,
        )}
        isStatNumberLoading={isLoading}
        {...(isOutputHighlighted
          ? {
              bg: "magenta.dark",
              color: "white",
            }
          : {})}
      >
        {outputContentAfter}
      </PricingCalculatorOutput>
    </PricingCalculatorSectionContainer>
  );
}
